import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { 
  MapPin, 
  Satellite, 
  ExternalLink 
} from 'lucide-react';

const customPinIcon = L.icon({
  iconUrl: '/images/custom-location-pin.png',
  iconSize: [32, 46],
  iconAnchor: [16, 46],
  popupAnchor: [0, -46],
  className: 'custom-location-pin-img',
});

// Satellite (Sputnik) Layers - High resolution Esri World Imagery + Places overlay
const SPUTNIK_LAYERS = [
  {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri World Imagery',
    maxZoom: 19,
  },
  {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
    maxZoom: 19,
  },
];

export default function InteractiveMapPicker({ latitude, longitude, onChange, height = '360px' }) {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerInstanceRef = useRef(null);

  const lat = parseFloat(latitude) || 40.160331;
  const lng = parseFloat(longitude) || 67.827246;

  // Initialize Map
  useEffect(() => {
    if (!mapContainerRef.current) return;

    if (!mapInstanceRef.current) {
      const map = L.map(mapContainerRef.current, {
        center: [lat, lng],
        zoom: 15,
        minZoom: 6,
        maxZoom: 19,
        doubleClickZoom: false, // Disables double-click zoom to enable double-click pin placement!
      });

      // Add Sputnik layers
      SPUTNIK_LAYERS.forEach((lCfg) => {
        L.tileLayer(lCfg.url, {
          attribution: lCfg.attribution || '',
          maxZoom: lCfg.maxZoom || 19,
        }).addTo(map);
      });

      // Draggable Marker
      const marker = L.marker([lat, lng], {
        icon: customPinIcon,
        draggable: true,
      }).addTo(map);

      marker.on('dragend', (e) => {
        const newPos = e.target.getLatLng();
        const nextLat = parseFloat(newPos.lat.toFixed(6));
        const nextLng = parseFloat(newPos.lng.toFixed(6));
        onChange({ latitude: nextLat, longitude: nextLng });
      });

      // Double-click on map to set position
      map.on('dblclick', (e) => {
        const clickLat = parseFloat(e.latlng.lat.toFixed(6));
        const clickLng = parseFloat(e.latlng.lng.toFixed(6));
        marker.setLatLng([clickLat, clickLng]);
        map.panTo([clickLat, clickLng]);
        onChange({ latitude: clickLat, longitude: clickLng });
      });

      mapInstanceRef.current = map;
      markerInstanceRef.current = marker;

      setTimeout(() => {
        map.invalidateSize();
      }, 300);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerInstanceRef.current = null;
      }
    };
  }, []);

  // Update marker when external lat/lng changes
  useEffect(() => {
    if (mapInstanceRef.current && markerInstanceRef.current) {
      const curPos = markerInstanceRef.current.getLatLng();
      if (Math.abs(curPos.lat - lat) > 0.0001 || Math.abs(curPos.lng - lng) > 0.0001) {
        markerInstanceRef.current.setLatLng([lat, lng]);
        mapInstanceRef.current.setView([lat, lng], mapInstanceRef.current.getZoom());
      }
    }
  }, [lat, lng]);

  return (
    <div className="space-y-2 bg-slate-50 p-2.5 sm:p-3 rounded-xl border border-slate-300 shadow-xs">
      
      {/* 1. TOP HEADER: Status & Coordinates */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 bg-slate-200/90 px-2.5 py-1 rounded-lg border border-slate-300">
          <Satellite className="w-3.5 h-3.5 text-[#b38038]" />
          <span className="text-[11px] font-bold text-slate-800">Sputnik (Sun'iy yo'ldosh xaritasi)</span>
        </div>

        <div className="flex items-center gap-2">
          {/* Coordinates readout */}
          <span className="text-[11px] font-mono text-slate-700 bg-white px-2.5 py-1 rounded-lg border border-slate-300 shrink-0">
            📍 {lat.toFixed(6)}, {lng.toFixed(6)}
          </span>

          <a
            href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] text-blue-700 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 border border-blue-200 px-2 py-1 rounded-lg font-semibold flex items-center gap-1 transition-colors"
            title="Ushbu nuqtani Google Maps'da ochish"
          >
            <span className="hidden sm:inline">Google Maps</span>
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>

      {/* 2. MAP CANVAS */}
      <div className="relative rounded-xl border-2 border-amber-400/90 overflow-hidden shadow-sm">
        <div
          ref={mapContainerRef}
          style={{ height, width: '100%' }}
          className="w-full relative z-0"
        />

        {/* Floating Sputnik Badge */}
        <div className="absolute top-2 right-2 z-10 pointer-events-none">
          <span className="bg-[#091322]/90 text-[#c5a880] text-[10px] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider backdrop-blur-xs border border-[#c5a880]/40 shadow-md flex items-center gap-1">
            <Satellite className="w-3 h-3" />
            <span>Sputnik</span>
          </span>
        </div>
      </div>

      {/* 3. INSTRUCTION FOOTER */}
      <div className="flex items-center justify-between text-[11px] text-slate-600 bg-amber-50/90 p-2.5 rounded-lg border border-amber-200">
        <span className="flex items-center leading-tight">
          <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2 shrink-0 animate-ping" />
          <span>
            <strong>Qo‘llanma:</strong> Xaritada yangi ofis joylashuvi ustiga <strong>2 marta bosing (double-click)</strong> yoki qizil pinni sudrab olib boring. Koordinatalar avtomatik saqlanadi.
          </span>
        </span>
      </div>

    </div>
  );
}
